import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root',
})
export class LangService {
	private readonly supportedLanguages = ['de', 'en', 'cs'];
	private readonly fontFamilies = ['AppFont', 'AppFontBold', 'AppFontLight'];
	private readonly defFontSrc = [
		'../assets/fonts/neutra_text_medium.otf',
		'../assets/fonts/neutra_text_bold.otf',
		'../assets/fonts/neutr_text_light.otf',
	];
	private readonly specFontSrc = [
		'../assets/fonts/JosefinSans-Regular.ttf',
		'../assets/fonts/JosefinSans-Bold.ttf',
		'../assets/fonts/JosefinSans-Light.ttf',
	];
	private loadedFonts: FontFace[] = [];

	constructor(private translate: TranslateService) {}

	init() {
		const languageValue = localStorage.getItem('langOfApp');
		if (languageValue) {
			// Migrate legacy Czech code (`cr`) to the correct `cs`.
			let normalizedLanguage = languageValue === 'cr' ? 'cs' : languageValue;
			// Fall back to `de` for languages without a shipped translation file
			// (e.g. stale `langOfApp` values from other SMOOTHR apps sharing storage).
			if (!this.supportedLanguages.includes(normalizedLanguage)) {
				normalizedLanguage = 'de';
			}
			if (normalizedLanguage !== languageValue) {
				localStorage.setItem('langOfApp', normalizedLanguage);
			}
			this.changeLanguage(normalizedLanguage);
		} else {
			this.changeLanguage('de');
		}

		this.translate.onLangChange.subscribe(languageChanged => {
			this.translate.setDefaultLang(languageChanged.lang);
			if (languageChanged) localStorage.setItem('langOfApp', languageChanged.lang);
		});
	}

	changeLanguage(language: string) {
		this.translate.use(language);
		this.loadFonts(language);
	}

	async loadFonts(lang: string) {
		const sources = lang === 'cs' ? this.specFontSrc : this.defFontSrc;

		// Remove previously loaded fonts so the new set takes effect.
		this.loadedFonts.forEach(f => (document.fonts as any).delete(f));
		this.loadedFonts = [];

		this.loadedFonts = await Promise.all(
			sources.map(async (src, i) => {
				const font = new FontFace(this.fontFamilies[i], `url(${src})`);
				await font.load();
				(document.fonts as any).add(font);
				return font;
			})
		);
	}
}
